import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import { AuthContext } from "../context/AuthContext";
import { getStatusBadgeClass } from "../utils/Utils";
import Loader from "../components/Loader";
import Modal from "../components/Modal";

const ViewTicket = () => {
  const { user } = useContext(AuthContext);
  const [isAdmin, setIsAdmin] = useState(user.role.toUpperCase() === "ADMIN");
  const { id } = useParams(); // Get the ticket ID from the URL
  const navigate = useNavigate(); // Replaces useHistory
  const [ticket, setTicket] = useState(null); // State to hold ticket details
  const [newMessage, setNewMessage] = useState(""); // State for new message
  const [fileAttachment, setFileAttachment] = useState(null); // State for file attachment
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeTicketLoading, setCloseTicketLoading] = useState(false);
  const currentUserId = user.id; // Replace with actual user ID (perhaps from authentication context)

  // API call to fetch ticket details based on ticketId
  useEffect(() => {
    const fetchTicketDetails = async () => {
      try {
        const response = await axiosInstance.get(`/tickets/${id}`); // Adjust URL as per your API
        setTicket(response.data);
      } catch (error) {
        console.error("Error fetching ticket details:", error);
      }
    };

    fetchTicketDetails();
  }, [id]);

  // Function to handle message submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("message", newMessage);
    formData.append("user_id", currentUserId);
    if (fileAttachment) {
      formData.append("file", fileAttachment);
    }

    try {
      await axiosInstance.post(`/tickets/${id}/messages`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await axiosInstance.get(`/tickets/${id}`);
      setTicket(response.data);
      setNewMessage("");
      setFileAttachment(null);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleCloseTicket = async () => {
    try {
      setCloseTicketLoading(true);
      await axiosInstance.put(`/tickets/${ticket.id}/close`);
      setCloseTicketLoading(false);
      navigate(isAdmin ? "/admin/all-tickets" : "/my-ticket");
    } catch (error) {
      console.error("Error while downloading the file:", error);
    }
  };

  // If ticket data is not yet loaded
  if (!ticket) {
    return (
      <div className="container mx-auto p-6">
        <Loader />
      </div>
    );
  }

  return (
    <div className="p-6 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-200 min-h-screen">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Ticket Details</h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        {/* Section 1: Ticket Information */}
        <div>
          <label className="block text-gray-700 dark:text-gray-300">
            Ticket ID:
          </label>
          <p className="text-lg">{ticket.ticket_id}</p>
        </div>

        {/* Section 2: Ticket Details */}
        <div>
          <label className="block text-gray-700 dark:text-gray-300">
            Subject:
          </label>
          <p className="text-lg">{ticket.subject}</p>
        </div>
        <div>
          <label className="block text-gray-700 dark:text-gray-300">
            Status:
          </label>
          <p className="text-lg">
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(
                ticket.status
              )}`}
            >
              {ticket.status}
            </span>
          </p>
        </div>

        {/* Section 3: Dates */}
        <div>
          <label className="block text-gray-700 dark:text-gray-300">
            Opening Date:
          </label>
          <p className="text-lg">{ticket.opening_date}</p>
        </div>
      </div>

      <br />

      {/* Ticket Messages Section */}
      <div className="mb-6">
        <h3 className="text-xl font-bold mb-4">Messages</h3>
        {ticket.ticket_messages && ticket.ticket_messages.length > 0 ? (
          <ul className="space-y-4">
            {ticket.ticket_messages.map((message) => {
              const isMyMessage = message.user_id === currentUserId;

              return (
                <li
                  key={message.id}
                  className={`relative group flex ${
                    isMyMessage ? "justify-end" : "justify-start"
                  }`}
                >
                  {/* Tooltip message box */}
                  <div
                    className={`max-w-[50%] p-4 bg-white border border-gray-300 shadow-lg dark:bg-gray-800 dark:border-gray-700 rounded-2xl transition-opacity duration-300 ${
                      isMyMessage
                        ? "bg-gray-200 text-gray-800"
                        : "bg-blue-200 text-blue-800"
                    }`}
                  >
                    <p className="text-sm text-gray-500">
                      {isMyMessage
                        ? "Message sent from Me"
                        : user.role.toUpperCase() === "ADMIN"
                        ? "Message sent from Client"
                        : "Message sent from Support Agent"}
                    </p>
                    <br />
                    <p>{message.message}</p>
                    <br />
                    {message.file_name && (
                      <p>
                        <strong>Attachments: </strong>
                        <a
                          className="text-blue-600 underline inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
                          title={message.file_name} // Show full name on hover
                        >
                          {message.file_name}
                        </a>
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-gray-700 dark:text-gray-300">No messages yet.</p>
        )}
      </div>

      <br />
      <br />

      <div className="flex justify-between mt-6">
        <div className="flex">
          <button
            onClick={() =>
              navigate(isAdmin ? "/admin/all-tickets" : "/my-ticket")
            }
            className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 mr-4"
          >
            Back to Tickets
          </button>
          {ticket.status !== "CLOSED" && (
            <button
              onClick={() => setShowCloseModal(true)} // Assuming you have a close ticket handler
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 active:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 transition-all shadow-md"
            >
              Close Ticket
            </button>
          )}
        </div>
      </div>

      {showCloseModal && (
        <Modal
          title="Close Ticket"
          content={`Are you sure you want to close the ticket?`}
          onConfirm={handleCloseTicket}
          onCancel={() => setShowCloseModal(false)}
          action={closeTicketLoading ? "Closing..." : "Yes"}
          disabled={closeTicketLoading}
        />
      )}
    </div>
  );
};

export default ViewTicket;
